import React, { useState, useMemo } from 'react';
import { releases as mockReleases, mockSoundCloudTracks } from '../mock';
import { Play, Music2, TrendingUp, Clock, Grid3x3, ExternalLink, ArrowUpDown } from 'lucide-react';
import AppleLogo from './AppleLogo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useMusic } from '../contexts/MusicContext';

const MusicSection = () => {
  // Use shared music context — single fetch for the entire app
  const musicData = useMusic();

  // Filter and sort state
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'popular', 'recent'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'plays', 'alphabetical'
  const [showAll, setShowAll] = useState(false);
  const fallbackArtwork = '/jlee3.png';

  // Process Apple Music releases
  const allReleases = musicData.apple_music_releases.length > 0 
    ? musicData.apple_music_releases
        .filter(release => release && release.title)
        .map((release, index) => ({
          id: release.id || `release_${index + 1}`,
          title: release.title || 'Untitled',
          type: release.type || 'Single',
          year: release.year || new Date().getFullYear().toString(),
          coverImage: release.coverImage || fallbackArtwork,
          appleMusicUrl: release.appleMusicUrl || `https://music.apple.com/ca/artist/jlee/1756341411`,
          soundcloudUrl: 'https://on.soundcloud.com/p7lR72fb7GYKSPuCUZ',
          platform: release.platform || 'Apple Music',
          trackCount: release.trackCount || 0
        }))
    : mockReleases;

  // Process and organize SoundCloud tracks
  // ALWAYS use fallback mock data if backend fails or returns empty
  const processedTracks = useMemo(() => {
    // Use backend data if available, otherwise use mock fallback
    const tracksToProcess = musicData.soundcloud_tracks.length > 0 
      ? musicData.soundcloud_tracks 
      : mockSoundCloudTracks;
    
    if (tracksToProcess.length === 0) {
      return [];
    }
    
    const processed = tracksToProcess
      .filter(track => {
        // Enhanced validation
        if (!track || typeof track !== 'object') {
          return false;
        }
        if (!track.title || !track.permalink_url) {
          return false;
        }
        if (!track.permalink_url.startsWith('http')) {
          return false;
        }
        return true;
      })
      .map(track => {
        // Parse created_at date
        let releaseDate = null;
        let year = null;
        if (track.created_at) {
          try {
            releaseDate = new Date(track.created_at);
            year = releaseDate.getFullYear().toString();
          } catch (e) {
            releaseDate = null;
            year = null;
          }
        }
        
        // Process artwork URL
        let artworkUrl = fallbackArtwork;
        if (track.artwork_url) {
          if (track.artwork_url.includes('sndcdn.com')) {
            artworkUrl = track.artwork_url
              .replace(/-\d+x\d+\./, '-t500x500.')
              .replace('large', 't500x500')
              .replace('t50x50', 't500x500')
              .replace('t120x120', 't500x500')
              .replace('t300x300', 't500x500');
          } else {
            artworkUrl = track.artwork_url;
          }
        }
        
        // Final validation before returning processed track
        const processedTrack = {
          id: String(track.id || `track_${Date.now()}_${Math.random()}`),
          title: String(track.title || 'Untitled Track').substring(0, 500),
          permalink_url: String(track.permalink_url || 'https://soundcloud.com/jessie-lawrence-103169919'),
          artwork_url: String(artworkUrl),
          playback_count: Math.max(0, parseInt(track.playback_count) || 0),
          favoritings_count: Math.max(0, parseInt(track.favoritings_count) || 0), // Likes count from SoundCloud
          genre: String(track.genre || '').substring(0, 100),
          created_at: String(track.created_at || ''),
          releaseDate: releaseDate,
          year: year || 'Unknown',
          platform: 'SoundCloud'
        };
        
        // Validate processed track
        if (!processedTrack.id || !processedTrack.title || !processedTrack.permalink_url) {
          return null;
        }
        
        return processedTrack;
      })
      .filter(track => track !== null); // Remove any null tracks from failed validation
    
    return processed;
  }, [musicData.soundcloud_tracks]);

  // Calculate SoundCloud stats
  const soundcloudStats = useMemo(() => {
    const totalPlays = processedTracks.reduce((sum, track) => sum + (track.playback_count || 0), 0);
    const totalTracks = processedTracks.length;
    const avgPlays = totalTracks > 0 ? Math.round(totalPlays / totalTracks) : 0;
    return {
      totalPlays,
      totalTracks,
      avgPlays
    };
  }, [processedTracks]);

  // Get featured track (most popular)
  const featuredTrack = useMemo(() => {
    if (processedTracks.length === 0) return null;
    return [...processedTracks].sort((a, b) => b.playback_count - a.playback_count)[0];
  }, [processedTracks]);

  // Only show the "Top Tracks" highlight reel when the catalog is large enough
  // that it adds value beyond the full list below. Below this, the full list
  // alone covers everything without duplicating the same players on screen.
  const TOP_TRACKS_MIN_CATALOG = 10;

  // Get top tracks (most popular - top 3-5), excluding the featured track
  // so the same player never renders twice.
  const topTracks = useMemo(() => {
    if (!featuredTrack || processedTracks.length < TOP_TRACKS_MIN_CATALOG) return [];
    return [...processedTracks]
      .filter(t => t.id !== featuredTrack.id)
      .sort((a, b) => b.playback_count - a.playback_count)
      .slice(0, 5)
      .filter(track => track.playback_count > 0);
  }, [processedTracks, featuredTrack]);

  // Tracks already shown in the prominent sections above. The full
  // SoundCloud Tracks list below excludes these so the same iframe
  // never renders more than once on the page.
  const excludedTrackIds = useMemo(() => {
    const ids = new Set();
    if (featuredTrack) ids.add(featuredTrack.id);
    topTracks.forEach(t => ids.add(t.id));
    return ids;
  }, [featuredTrack, topTracks]);

  const remainingTracks = useMemo(
    () => processedTracks.filter(t => !excludedTrackIds.has(t.id)),
    [processedTracks, excludedTrackIds]
  );

  // Helper function to apply sorting to tracks
  const applySorting = (tracks, sortByValue) => {
    const sorted = [...tracks];
    if (sortByValue === 'plays') {
      sorted.sort((a, b) => b.playback_count - a.playback_count);
    } else if (sortByValue === 'alphabetical') {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    } else { // date (default)
      sorted.sort((a, b) => {
        if (!a.releaseDate && !b.releaseDate) return 0;
        if (!a.releaseDate) return 1;
        if (!b.releaseDate) return -1;
        return b.releaseDate - a.releaseDate; // Newest first
      });
    }
    return sorted;
  };

  const recentWindowDays = 180;
  const recentMaxTracks = 5;
  const popularMaxTracks = 5;

  // Filter and sort tracks
  const filteredAndSortedTracks = useMemo(() => {
    let tracks = [...remainingTracks];

    // Apply filter
    if (activeFilter === 'popular') {
      // Popular: top tracks by plays
      tracks = tracks.filter(t => t.playback_count > 0);
      tracks = applySorting(tracks, 'plays').slice(0, popularMaxTracks);
    } else if (activeFilter === 'recent') {
      // Recent: tracks from last 180 days (fallback to newest if none match)
      const recentCutoff = new Date(Date.now() - recentWindowDays * 24 * 60 * 60 * 1000);
      let recentTracks = tracks.filter(t => t.releaseDate && t.releaseDate > recentCutoff);
      if (recentTracks.length === 0) {
        recentTracks = applySorting(tracks, 'date').slice(0, recentMaxTracks);
      } else {
        recentTracks = applySorting(recentTracks, 'date').slice(0, recentMaxTracks);
      }
      tracks = recentTracks;
    }

    // Apply sort (only if not already sorted by popular filter)
    if (activeFilter !== 'popular') {
      tracks = applySorting(tracks, sortBy);
    }

    return tracks;
  }, [remainingTracks, activeFilter, sortBy, popularMaxTracks, recentWindowDays, recentMaxTracks]);

  // Group tracks by year (after filters)
  const tracksByYear = useMemo(() => {
    const grouped = {};
    filteredAndSortedTracks.forEach(track => {
      const year = track.year || 'Unknown';
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(track);
    });
    return grouped;
  }, [filteredAndSortedTracks]);

  // Get sorted years (newest first)
  const sortedYears = useMemo(() => {
    return Object.keys(tracksByYear).sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return parseInt(b) - parseInt(a);
    });
  }, [tracksByYear]);

  // Display tracks (limit to 12 initially, show all if showAll is true)
  const displayTracks = showAll ? filteredAndSortedTracks : filteredAndSortedTracks.slice(0, 12);
  const hasMoreTracks = filteredAndSortedTracks.length > 12;

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      const now = new Date();
      const diffTime = Math.abs(now - d);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Show weeks for 1-4 weeks
      if (diffDays >= 7 && diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} ${months === 1 ? 'month' : 'months'} ago`;
      } else {
        return d.getFullYear().toString();
      }
    } catch {
      return '';
    }
  };

  // Get SoundCloud embed URL
  const getSoundCloudEmbedUrl = (trackUrl) => {
    const encodedUrl = encodeURIComponent(trackUrl);
    return `https://w.soundcloud.com/player/?url=${encodedUrl}&color=%2300ffd1&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`;
  };

  // Get Apple Music embed URL
  const getAppleMusicEmbedUrl = (url) => {
    if (!url || !url.includes('music.apple.com')) {
      return '';
    }
    // Convert Apple Music URL to embed format
    // Example: https://music.apple.com/ca/album/album-name/123456789
    // Becomes: https://embed.music.apple.com/ca/album/album-name/123456789
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      // Replace /music.apple.com/ with /embed.music.apple.com/
      return `https://embed.music.apple.com${path}`;
    } catch (e) {
      return '';
    }
  };

  return (
    <section className="py-32 bg-[#121212] relative" id="music">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-[7.6923%]">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <p id="music-section-tab" className="section-tab">Discography</p>
          <h2 id="music-section-title" className="section-title">Music & Productions</h2>
          <p className="body-medium text-white/85 max-w-3xl mx-auto leading-relaxed mb-4">
            Professional DJ and producer based in Kelowna, BC, with a proven track record of original releases and live performances. 
            Multiple albums and EPs available on Apple Music, plus an extensive catalog of tracks on SoundCloud 
            spanning dubstep, bass music, and electronic genres.
          </p>
          <p className="body-medium text-white/75 max-w-3xl mx-auto leading-relaxed">
            <strong className="text-white">Available for bookings</strong> — DJ sets at clubs, bars, festivals, and private events throughout Kelowna, the Okanagan, and across Canada. 
            Versatile performance styles from high-energy dance sets to ambient chill vibes, tailored to your venue and audience.
          </p>
        </div>

        {/* SoundCloud Stats Card */}
        {processedTracks.length > 0 && (
          <div className="mb-12 box-card p-6 md:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center">
                <p className="display-medium text-brand-turquoise mb-2">
                  {soundcloudStats.totalTracks}
                </p>
                <p className="body-medium text-white/70">Tracks</p>
              </div>
              <div className="text-center">
                <p className="display-medium text-brand-turquoise mb-2">
                  {soundcloudStats.totalPlays.toLocaleString()}
                </p>
                <p className="body-medium text-white/70">Total Plays</p>
              </div>
              <div className="text-center">
                <p className="display-medium text-brand-turquoise mb-2">
                  {soundcloudStats.avgPlays.toLocaleString()}
                </p>
                <p className="body-medium text-white/70">Avg Plays</p>
              </div>
            </div>
            <div className="mt-6 text-center">
                <a
                  id="music-follow-soundcloud-link"
                  href="https://soundcloud.com/jessie-lawrence-103169919"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-turquoise body-medium hover:underline flex items-center justify-center gap-2 music-link-visible"
                >
                  <ExternalLink size={16} />
                  <span id="music-follow-soundcloud-text" className="music-link-text music-text-nowrap">Follow on SoundCloud</span>
                </a>
            </div>
          </div>
        )}

        {/* Featured Track Banner */}
        {featuredTrack && featuredTrack.playback_count > 0 && (
          <div className="mb-16 box-card overflow-hidden">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 p-6 md:p-8">
              <div className="relative aspect-square">
                {featuredTrack.artwork_url && (
                  <img
                    src={featuredTrack.artwork_url}
                    alt={featuredTrack.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      if (e.target.src !== fallbackArtwork) {
                        e.target.src = fallbackArtwork;
                      }
                    }}
                  />
                )}
                <div className="absolute top-4 left-4">
                  <span className="music-player-button">
                    Most Popular
                  </span>
                </div>
              </div>
              <div className="flex flex-col justify-center space-y-4 md:space-y-6">
                <div>
                  <h3 id="music-featured-track-title" className="subsection-title mb-2 drop-shadow-lg">{featuredTrack.title}</h3>
                  <div className="flex items-center gap-4 body-medium mb-2">
                    <span className="text-white font-semibold">
                      {featuredTrack.playback_count.toLocaleString()} plays
                      {featuredTrack.favoritings_count > 0 && (
                        <> • {featuredTrack.favoritings_count.toLocaleString()} {featuredTrack.favoritings_count === 1 ? 'like' : 'likes'}</>
                      )}
                    </span>
                    {featuredTrack.genre && (
                      <span className="music-tag">{featuredTrack.genre}</span>
                    )}
                  </div>
                  {featuredTrack.year && featuredTrack.year !== 'Unknown' && (
                    <p className="body-medium text-white font-semibold">{featuredTrack.year}</p>
                  )}
                </div>
                <div className="w-full soundcloud-player-wrapper">
                  {featuredTrack.permalink_url && featuredTrack.permalink_url.startsWith('http') ? (
                    <iframe
                      width="100%"
                      height="166"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay; encrypted-media"
                      src={getSoundCloudEmbedUrl(featuredTrack.permalink_url)}
                      className="w-full rounded-lg"
                      title={`SoundCloud player for ${featuredTrack.title || 'Track'}`}
                      onError={(e) => {
                        // Suppress iframe errors - they're handled by SoundCloud widget
                        e.stopPropagation();
                      }}
                      onLoad={() => {
                        // Silently handle load - widget may have internal errors that don't affect playback
                      }}
                    />
                  ) : (
                    <div className="w-full h-[166px] bg-black/50 border border-white/10 rounded-lg flex items-center justify-center">
                      <p className="text-white/50 text-sm">Invalid track URL</p>
                    </div>
                  )}
                </div>
                <a
                  id="music-featured-open-link"
                  href={featuredTrack.permalink_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-turquoise body-small hover:underline flex items-center gap-2 font-semibold music-link-visible"
                >
                  <ExternalLink size={16} className="text-brand-turquoise" />
                  <span id="music-featured-open-text" className="music-link-text text-brand-turquoise music-text-nowrap">Open on SoundCloud</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Top Tracks Section */}
        {topTracks.length > 0 && (
          <div className="mb-16">
            <div className="mb-8">
              <h3 id="music-top-tracks-title" className="subsection-title mb-2">Top Tracks</h3>
              <p className="body-medium text-white/70">Most played tracks</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topTracks.map((track) => (
                <div
                  key={track.id}
                  className="box-card p-6 space-y-4"
                >
                  <div className="flex items-start gap-4">
                    {track.artwork_url && (
                      <img
                        src={track.artwork_url}
                        alt={track.title}
                        className="w-16 h-16 object-cover flex-shrink-0"
                        loading="lazy"
                        onError={(e) => {
                          if (e.target.src !== fallbackArtwork) {
                            e.target.src = fallbackArtwork;
                          }
                        }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h5 className="subsection-title mb-1 line-clamp-2 music-heading-sm">
                        {track.title}
                      </h5>
                      <div className="flex items-center gap-4 text-white/70 body-small mb-2">
                        <span>
                          {track.playback_count.toLocaleString()} plays
                          {track.favoritings_count > 0 && (
                            <> • {track.favoritings_count.toLocaleString()} {track.favoritings_count === 1 ? 'like' : 'likes'}</>
                          )}
                        </span>
                        {track.genre && (
                          <span className="music-tag">{track.genre}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-full soundcloud-player-wrapper">
                    {track.permalink_url && track.permalink_url.startsWith('http') ? (
                      <iframe
                        width="100%"
                        height="166"
                        scrolling="no"
                        frameBorder="no"
                        allow="autoplay; encrypted-media"
                        src={getSoundCloudEmbedUrl(track.permalink_url)}
                        className="w-full rounded-lg"
                        title={`SoundCloud player for ${track.title || 'Track'}`}
                        onError={(e) => {
                          // Suppress iframe errors - they're handled by SoundCloud widget
                          e.stopPropagation();
                        }}
                        onLoad={() => {
                          // Silently handle load - widget may have internal errors that don't affect playback
                        }}
                      />
                    ) : (
                      <div className="w-full h-[166px] bg-black/50 border border-white/10 rounded-lg flex items-center justify-center">
                        <p className="text-white/50 text-sm">Invalid track URL</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SOUNDCLOUD TRACKS — the rest of the catalog after Featured/Top Tracks */}
        {(musicData.loading || remainingTracks.length > 0 || processedTracks.length === 0) && (
        <div className="mb-16">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <h3 id="music-soundcloud-tracks-title" className="subsection-title">SoundCloud Tracks</h3>
              
              {/* Filter Tabs */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`music-filter-button music-filter-button-fixed ${activeFilter === 'all' ? 'music-filter-button-active' : 'music-filter-button-inactive'}`}
                >
                  <Grid3x3 size={16} className="flex-shrink-0" />
                  <span id="music-filter-all-text" className="music-filter-text music-text-nowrap">All ({remainingTracks.length})</span>
                </button>
                <button
                  onClick={() => setActiveFilter('popular')}
                  className={`music-filter-button music-filter-button-fixed ${activeFilter === 'popular' ? 'music-filter-button-active' : 'music-filter-button-inactive'}`}
                >
                  <TrendingUp size={16} className="flex-shrink-0" />
                  <span id="music-filter-popular-text" className="music-filter-text music-text-nowrap">Popular</span>
                </button>
                <button
                  onClick={() => setActiveFilter('recent')}
                  className={`music-filter-button music-filter-button-fixed ${activeFilter === 'recent' ? 'music-filter-button-active' : 'music-filter-button-inactive'}`}
                >
                  <Clock size={16} className="flex-shrink-0" />
                  <span id="music-filter-recent-text" className="music-filter-text music-text-nowrap">Recent</span>
                </button>
                
                {/* Sort Dropdown */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger 
                    id="music_sort"
                    name="music_sort"
                    aria-label="Sort music tracks"
                    className="music-filter-button music-filter-button-fixed music-filter-button-inactive !w-auto music-sort-trigger-base"
                  >
                    <ArrowUpDown size={16} className="flex-shrink-0 text-white" />
                    <SelectValue id="music-sort-value">
                      <span id="music-sort-text" className="music-sort-text music-text-nowrap">
                        {sortBy === 'date' ? 'Date' : sortBy === 'plays' ? 'Plays' : 'A-Z'}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/30 text-white rounded-none shadow-lg z-50">
                    <SelectItem value="date" id="music-sort-date" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">
                      <span className="music-sort-option-text music-text-nowrap">Date</span>
                    </SelectItem>
                    <SelectItem value="plays" id="music-sort-plays" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">
                      <span className="music-sort-option-text music-text-nowrap">Plays</span>
                    </SelectItem>
                    <SelectItem value="alphabetical" id="music-sort-alphabetical" className="text-white hover:bg-white/20 focus:bg-white/20 data-[highlighted]:bg-white/20 cursor-pointer py-3">
                      <span className="music-sort-option-text music-text-nowrap">A-Z</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Filter summary */}
            {!musicData.loading && remainingTracks.length > 0 && (
              <p className="body-small text-white/60 mb-4">
                Showing {filteredAndSortedTracks.length} of {remainingTracks.length} tracks
              </p>
            )}

            {/* Grouped by Year - Show ALL tracks, no limit */}
            {musicData.loading ? (
              <div className="text-center py-12">
                <p className="body-medium text-white/70">Loading SoundCloud tracks from https://soundcloud.com/jessie-lawrence-103169919...</p>
              </div>
            ) : filteredAndSortedTracks.length > 0 ? (
              <div className="space-y-12">
                {sortedYears.map((year) => {
                  const yearTracks = tracksByYear[year] || [];

                  if (yearTracks.length === 0) return null;

                  return (
                    <div key={year} className="space-y-6">
                      <h4 className="subsection-title border-b border-white/10 pb-2">
                        {year}
                      </h4>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {yearTracks.map((track) => (
                          <div
                            key={track.id}
                            className="box-card p-6 space-y-4"
                          >
                            {/* Track Info */}
                            <div className="flex items-start gap-4">
                              {track.artwork_url && (
                                <img
                                  src={track.artwork_url}
                                  alt={track.title}
                                  className="w-20 h-20 object-cover flex-shrink-0"
                                  loading="lazy"
                                  onError={(e) => {
                                    if (e.target.src !== fallbackArtwork) {
                                      e.target.src = fallbackArtwork;
                                    }
                                  }}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h5 className="subsection-title mb-1 line-clamp-2 music-heading-sm">
                                  {track.title}
                                </h5>
                                <div className="flex items-center gap-4 text-white/70 body-small mb-2">
                                  <span>{track.playback_count.toLocaleString()} {track.playback_count === 1 ? 'play' : 'plays'}</span>
                                  {track.genre && (
                                    <span className="music-tag">{track.genre}</span>
                                  )}
                                </div>
                                <a
                                  href={track.permalink_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-turquoise body-small hover:underline flex items-center gap-1"
                                >
                                  <ExternalLink size={14} />
                                  Open on SoundCloud
                                </a>
                              </div>
                            </div>

                            {/* Embedded Player */}
                            <div className="w-full soundcloud-player-wrapper">
                              {track.permalink_url && track.permalink_url.startsWith('http') ? (
                                <iframe
                                  width="100%"
                                  height="166"
                                  scrolling="no"
                                  frameBorder="no"
                                  allow="autoplay; encrypted-media"
                                  src={getSoundCloudEmbedUrl(track.permalink_url)}
                                  className="w-full rounded-lg"
                                  title={`SoundCloud player for ${track.title || 'Track'}`}
                                  onError={(e) => {
                                    // Suppress iframe errors - they're handled by SoundCloud widget
                                    e.stopPropagation();
                                  }}
                                  onLoad={() => {
                                    // Silently handle load - widget may have internal errors that don't affect playback
                                  }}
                                />
                              ) : (
                                <div className="w-full h-[166px] bg-black/50 border border-white/10 rounded-lg flex items-center justify-center">
                                  <p className="text-white/50 text-sm">Invalid track URL</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* No tracks found */
              <div className="box-card p-8 text-center">
                {processedTracks.length === 0 ? (
                  <>
                    <p className="body-medium text-white/70 mb-4">
                      No SoundCloud tracks found. The backend may need to fetch tracks from SoundCloud.
                    </p>
                    <p className="body-small text-white/50">
                      Make sure SOUNDCLOUD_USER_ID is set in backend/.env or SOUNDCLOUD_CLIENT_ID is configured.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="body-medium text-white/70 mb-4">
                      No tracks match this filter.
                    </p>
                    {activeFilter === 'recent' && (
                      <p className="body-small text-white/50">
                        No tracks posted in the last {recentWindowDays} days.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Fallback: Grid View if no year grouping (old code, keeping for reference) */}
            {false && sortedYears.length === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {displayTracks.map((track) => (
                  <a
                    key={track.id}
                    href={track.permalink_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group box-card overflow-hidden cursor-pointer block"
                  >
                    <div className="relative">
                      <img
                        src={track.artwork_url}
                        alt={track.title}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                        onError={(e) => {
                          if (e.target.src !== fallbackArtwork) {
                            e.target.src = fallbackArtwork;
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-turquoise-solid w-12 h-12 flex items-center justify-center hover:scale-110 transition-transform">
                          <Play size={20} className="ml-1" />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="subsection-title group-hover:text-brand-turquoise transition-colors mb-1 line-clamp-2 music-heading-md">
                        {track.title}
                      </h4>
                      <div className="flex items-center justify-between text-white/70 body-small mb-2">
                        <span>
                          {track.playback_count.toLocaleString()} {track.playback_count === 1 ? 'play' : 'plays'}
                          {track.favoritings_count > 0 && (
                            <> • {track.favoritings_count.toLocaleString()} {track.favoritings_count === 1 ? 'like' : 'likes'}</>
                          )}
                        </span>
                        {track.created_at && (
                          <span>{formatDate(track.created_at)}</span>
                        )}
                      </div>
                      {track.genre && (
                        <p className="music-tag body-small">{track.genre}</p>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}

            {/* Load More Button (only for grid view) */}
            {sortedYears.length === 0 && hasMoreTracks && !showAll && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowAll(true)}
                  className="btn-secondary"
                >
                  <span className="text-white">View All {filteredAndSortedTracks.length} Tracks</span>
                </button>
              </div>
            )}

            {/* Show Less Button */}
            {sortedYears.length === 0 && showAll && hasMoreTracks && (
              <div className="text-center mt-8">
                <button
                  onClick={() => {
                    setShowAll(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="btn-secondary"
                >
                  <span className="text-white">Show Less</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* APPLE MUSIC RELEASES - Shown After SoundCloud */}
        {allReleases.length > 0 && (
          <div className="mt-16">
            {/* Section Header */}
            <div className="mb-12 text-center">
              <p id="music-releases-tab" className="section-tab">Releases</p>
              <h2 id="music-releases-title" className="section-title">Albums & EPs</h2>
            </div>
            
            {/* Featured Release */}
            {allReleases.length > 0 && (
              <div className="mb-12 box-card overflow-hidden">
                <div className="grid md:grid-cols-2 gap-6 md:gap-8 p-4 md:p-8">
                  <div className="relative aspect-square md:aspect-auto md:h-[400px]">
                    <img
                      src={allReleases[0].coverImage || fallbackArtwork}
                      alt={allReleases[0].title || 'Release'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        if (e.target.src !== fallbackArtwork) {
                          e.target.src = fallbackArtwork;
                        }
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span 
                        className="px-4 py-2 body-small font-semibold music-filter-button-active"
                      >
                        {allReleases[0].type}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center space-y-4 md:space-y-6 mt-6 md:mt-0">
                    <div>
                      <h3 id="music-release-title" className="subsection-title mb-2">{allReleases[0].title || 'Release'}</h3>
                      <p className="body-medium text-white/70">{allReleases[0].year || new Date().getFullYear().toString()}</p>
                    </div>
                    {allReleases[0].trackCount && (
                      <div>
                        <p className="body-medium text-brand-turquoise">
                          {allReleases[0].trackCount} {allReleases[0].trackCount === 1 ? 'Track' : 'Tracks'}
                        </p>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <a
                        href={allReleases[0].appleMusicUrl || 'https://music.apple.com/ca/artist/jlee/1756341411'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary flex-1 sm:flex-none"
                      >
                        <AppleLogo size={18} className="flex-shrink-0" color="#FFFFFF" />
                        <span className="text-white">Apple Music</span>
                      </a>
                      <a
                        href={allReleases[0].soundcloudUrl || 'https://on.soundcloud.com/p7lR72fb7GYKSPuCUZ'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary flex-1 sm:flex-none"
                      >
                        <Play size={18} className="text-white flex-shrink-0" />
                        <span className="text-white">SoundCloud</span>
                      </a>
                    </div>
                    
                    {/* Apple Music Player */}
                    {allReleases[0].appleMusicUrl && getAppleMusicEmbedUrl(allReleases[0].appleMusicUrl) && (
                      <div className="w-full mt-6">
                        <h4 className="body-medium text-white/70 mb-3">Listen on Apple Music</h4>
                        <div className="w-full soundcloud-player-wrapper">
                          <iframe
                            allow="autoplay *; encrypted-media *;"
                            frameBorder="0"
                            height="450"
                            style={{ width: '100%', maxWidth: '660px', overflow: 'hidden', background: 'transparent' }}
                            sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
                            src={getAppleMusicEmbedUrl(allReleases[0].appleMusicUrl)}
                            title={`Apple Music player for ${allReleases[0].title || 'Release'}`}
                            className="w-full rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Featured Releases with Players (Next 2 releases - showing 3 total with the first one) */}
            {allReleases.length > 1 && (
              <div className="space-y-12 mb-12">
                {allReleases.slice(1, 3).map((release) => (
                  <div key={release.id} className="box-card overflow-hidden">
                    <div className="grid md:grid-cols-2 gap-6 md:gap-8 p-4 md:p-8">
                      <div className="relative aspect-square md:aspect-auto md:h-[400px]">
                        <img
                          src={release.coverImage || fallbackArtwork}
                          alt={release.title || 'Release'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            if (e.target.src !== fallbackArtwork) {
                              e.target.src = fallbackArtwork;
                            }
                          }}
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-4 py-2 body-small font-semibold music-filter-button-active">
                            {release.type}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col justify-center space-y-4 md:space-y-6 mt-6 md:mt-0">
                        <div>
                          <h3 className="subsection-title mb-2">{release.title || 'Release'}</h3>
                          <p className="body-medium text-white/70">{release.year || new Date().getFullYear().toString()}</p>
                        </div>
                        {release.trackCount && (
                          <div>
                            <p className="body-medium text-brand-turquoise">
                              {release.trackCount} {release.trackCount === 1 ? 'Track' : 'Tracks'}
                            </p>
                          </div>
                        )}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <a
                            href={release.appleMusicUrl || 'https://music.apple.com/ca/artist/jlee/1756341411'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary flex-1 sm:flex-none"
                          >
                            <AppleLogo size={18} className="flex-shrink-0" color="#FFFFFF" />
                            <span className="text-white">Apple Music</span>
                          </a>
                          <a
                            href={release.soundcloudUrl || 'https://on.soundcloud.com/p7lR72fb7GYKSPuCUZ'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary flex-1 sm:flex-none"
                          >
                            <Play size={18} className="text-white flex-shrink-0" />
                            <span className="text-white">SoundCloud</span>
                          </a>
                        </div>
                        
                        {/* Apple Music Player */}
                        {release.appleMusicUrl && getAppleMusicEmbedUrl(release.appleMusicUrl) && (
                          <div className="w-full mt-6">
                            <h4 className="body-medium text-white/70 mb-3">Listen on Apple Music</h4>
                            <div className="w-full soundcloud-player-wrapper">
                              <iframe
                                allow="autoplay *; encrypted-media *;"
                                frameBorder="0"
                                height="450"
                                style={{ width: '100%', maxWidth: '660px', overflow: 'hidden', background: 'transparent' }}
                                sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
                                src={getAppleMusicEmbedUrl(release.appleMusicUrl)}
                                title={`Apple Music player for ${release.title || 'Release'}`}
                                className="w-full rounded-lg"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Other Releases Grid (4th and beyond) */}
            {allReleases.length > 3 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {allReleases.slice(3).map((release) => (
                  <div
                    key={release.id}
                    className="group box-card overflow-hidden"
                  >
                    <div className="relative">
                      <img
                        src={release.coverImage || fallbackArtwork}
                        alt={release.title || 'Release'}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                        onError={(e) => {
                          if (e.target.src !== fallbackArtwork) {
                            e.target.src = fallbackArtwork;
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {release.appleMusicUrl && (
                          <a
                            href={release.appleMusicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-turquoise-solid w-12 h-12 flex items-center justify-center hover:scale-110 transition-transform"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Listen to ${release.title} on Apple Music`}
                          >
                            <AppleLogo size={20} color="#FFFFFF" />
                          </a>
                        )}
                        {release.soundcloudUrl && (
                          <a
                            href={release.soundcloudUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-turquoise-solid w-12 h-12 flex items-center justify-center hover:scale-110 transition-transform"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Listen to ${release.title} on SoundCloud`}
                          >
                            <Play size={20} className="ml-1" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="subsection-title group-hover:text-brand-turquoise transition-colors mb-1 music-heading-md">
                        {release.title || 'Untitled Release'}
                      </h4>
                      <p className="body-small text-white/70 mb-2">{release.year || '2024'}</p>
                      <p className="body-small text-[var(--brand-turquoise)]/70">{release.platform || 'Music'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default MusicSection;
